/**
 * Centralized react-query keys for the PMS app.
 *
 * Every query/mutation in @clarix/services-pms derives its key from this
 * factory, so invalidating `pmsKeys.all` flushes every PMS cache entry.
 * Per-domain methods added per milestone.
 */
export const pmsKeys = {
  all: ['pms'] as const,

  // ── M1: Masters ────────────────────────────────────────────────────
  categories: () => [...pmsKeys.all, 'categories'] as const,

  competencies: (params?: unknown) => [...pmsKeys.all, 'competencies', params ?? {}] as const,
  /**
   * PREFIX key — every parameterised competency list at once.
   *
   * `competencies()` returns `['pms','competencies',{}]`, and react-query matches
   * key elements EXACTLY, so invalidating with it only hit a list whose params
   * were literally `{}`. The page passes filters and pagination, so a bulk import
   * wrote 13 rows and the table behind the dialog never refetched — the data was
   * there, and only a page reload showed it.
   */
  competenciesAll: () => [...pmsKeys.all, 'competencies'] as const,

  map: (designationId: string | number) => [...pmsKeys.all, 'map', String(designationId)] as const,

  mapValidation: (designationId: string | number) =>
    [...pmsKeys.all, 'map', String(designationId), 'validation'] as const,

  bands: () => [...pmsKeys.all, 'bands'] as const,
  /** Params are part of the key — the date filter changes the result set. */
  aiAudit: (params?: object) => [...pmsKeys.all, 'ai-audit', params ?? {}] as const,

  // ── M2: Form builder ───────────────────────────────────────────────
  forms: (params?: unknown) => [...pmsKeys.all, 'forms', params ?? {}] as const,

  form: (templateId: string | number) => [...pmsKeys.all, 'forms', String(templateId)] as const,

  formRunPreview: (templateId: string | number) =>
    [...pmsKeys.all, 'forms', String(templateId), 'run-preview'] as const,

  formGate: (templateId: string | number) =>
    [...pmsKeys.all, 'forms', String(templateId), 'gate-check'] as const,

  formQuestions: (templateId: string | number) =>
    [...pmsKeys.all, 'forms', String(templateId), 'questions'] as const,

  // ── M3: Run management ─────────────────────────────────────────────
  audit: (params?: unknown) => [...pmsKeys.all, 'audit', params ?? {}] as const,
  auditEventTypes: () => [...pmsKeys.all, 'audit', 'event-types'] as const,
  runs: (params?: unknown) => [...pmsKeys.all, 'runs', params ?? {}] as const,

  run: (runId: string | number) => [...pmsKeys.all, 'runs', String(runId)] as const,

  runPreview: (runId: string | number) =>
    [...pmsKeys.all, 'runs', String(runId), 'preview'] as const,

  // ── M5: Team (RM / observer) evaluations ───────────────────────────
  teamEvaluations: (params?: unknown) =>
    [...pmsKeys.all, 'team', 'evaluations', params ?? {}] as const,

  teamEvaluation: (participantId: string | number) =>
    [...pmsKeys.all, 'team', 'evaluations', String(participantId)] as const,

  // ── M4: Employee self-evaluation ───────────────────────────────────
  // A separate namespace from `team`, not a filter on it: FR-410's dual hat
  // means the same person's two lists must invalidate independently.
  myEvaluations: (params?: unknown) => [...pmsKeys.all, 'my', 'evaluations', params ?? {}] as const,

  myEvaluation: (participantId: string | number) =>
    [...pmsKeys.all, 'my', 'evaluations', String(participantId)] as const,

  // ── M4: the public tokenised link (§12) ────────────────────────────
  // Keyed by TOKEN, not participant: the signed-out page never learns a
  // participant id until the preview resolves.
  tokenPreview: (token: string) => [...pmsKeys.all, 'token', token] as const,

  // ── D-27: the public MANAGER link ──────────────────────────────────
  // Two keys, because one link has two reads: the roster behind it, and one
  // reportee's form. The form key nests under the token so switching reportee
  // caches each separately and moving back is instant.
  rmLinkReportees: (token: string) => [...pmsKeys.all, 'rm-link', token] as const,
  rmLinkPreview: (token: string, participantId: string) =>
    [...pmsKeys.all, 'rm-link', token, 'participant', participantId] as const,

  // ── M6: Comparison · 1:1 · Conciliation ────────────────────────────
  comparison: (participantId: string | number) =>
    [...pmsKeys.all, 'comparison', String(participantId)] as const,

  /**
   * The 1:1 recording resource — a separate child of the comparison. Keyed
   * separately so a recording status poll does not re-render the comparison view,
   * and so `complete`/`abort` can invalidate recording + comparison independently.
   */
  recording: (participantId: string | number) =>
    [...pmsKeys.all, 'comparison', String(participantId), 'recording'] as const,

  // ── M8: RBAC ───────────────────────────────────────────────────────
  myAccess: () => [...pmsKeys.all, 'me', 'permissions'] as const,

  // ── In-app notifications (D-25) ────────────────────────────────────────────
  // Caller-scoped server-side, so no user id in the key: there is only ever ONE
  // person's feed in a session's cache.
  notifications: (params?: unknown) =>
    [...pmsKeys.all, 'notifications', 'list', params ?? {}] as const,
  notificationUnreadCount: () => [...pmsKeys.all, 'notifications', 'unread-count'] as const,
  notificationTypes: () => [...pmsKeys.all, 'notifications', 'types'] as const,
  /** The MASTER — workspace-wide config, not this user's feed. */
  notificationSettings: () => [...pmsKeys.all, 'notifications', 'settings'] as const,
  roleTemplates: () => [...pmsKeys.all, 'rbac', 'roles'] as const,
  userRoles: () => [...pmsKeys.all, 'rbac', 'user-roles'] as const,

  // ── M7: the role-dispatched dashboard ──────────────────────────────
  // No parameter: the body depends on WHO is asking, and that is the session,
  // not an argument.
  /**
   * Keyed by the day filter — a different window is a different body, so a
   * shared key would serve the previous range's numbers while the new one
   * loaded.
   */
  dashboard: (range?: { from?: string; to?: string }) =>
    [...pmsKeys.all, 'dashboard', range?.from ?? '', range?.to ?? ''] as const,
  /**
   * The PREFIX of every dashboard window, for invalidation.
   *
   * Because the range is part of the key, invalidating one window leaves every
   * other cached window stale — and the range control switches between them
   * without refetching. Anything that moves the numbers has to clear them ALL,
   * so it invalidates this rather than one `dashboard(range)`.
   */
  dashboardAll: () => [...pmsKeys.all, 'dashboard'] as const,
} as const;
